import { z } from "zod";

import { COGNITO_INTERNAL_STAFF_GROUPS } from "@recobra/core";

import { CaseContactType, CaseHistoryField } from "../case";
import { CaseStep, CaseType } from "../case-core";
import { DocumentConfidenceLevelSchema, DocumentTypeSchema } from "../document";
import { NoteMentionSchema } from "../note";
import { TaskAiAnalysisSchema, TaskHistoryField, TaskPriority, TaskStatus } from "../task";

const internalStaff = COGNITO_INTERNAL_STAFF_GROUPS;

const CaseHistoryActorSchema = z.object({
  kind: z
    .enum(["user", "automation"])
    .describe(
      "Worker/EventBridge-triggered mutations carry a provenance kind of 'system'; that and " +
        "'automation' both collapse to 'automation' here so the timeline shows one actor for both.",
    ),
  userId: z
    .string()
    .nullable()
    .describe("Identifier resolvable through /profile/lookup; null for automation actors"),
  name: z.string().nullable().describe("Denormalized fallback used when the profile lookup misses"),
});
export type CaseHistoryActor = z.infer<typeof CaseHistoryActorSchema>;

const caseHistoryEntry = <Type extends string, Data extends z.ZodType>(type: Type, data: Data) =>
  z.object({
    id: z.string().min(1).describe("Deterministic and unique across the whole feed"),
    type: z.literal(type),
    occurredAt: z.iso.datetime(),
    actor: CaseHistoryActorSchema,
    data,
  });

const CaseHistoryEntrySchema = z
  .discriminatedUnion("type", [
    caseHistoryEntry(
      "case_created",
      z.object({ caseNumber: z.number().int(), caseType: CaseType, step: CaseStep }),
    ),
    caseHistoryEntry(
      "case_step_changed",
      z.object({
        fromStep: CaseStep.nullable().describe("Null when the previous step was not recorded"),
        toStep: CaseStep,
      }),
    ),
    caseHistoryEntry(
      "case_fields_updated",
      z.object({
        updatedFields: z
          .array(CaseHistoryField)
          .describe("Stable identifiers for the case fields touched; never their values"),
      }),
    ),
    caseHistoryEntry(
      "case_note_added",
      z.object({
        noteId: z.string(),
        text: z.string(),
        mentions: z.array(NoteMentionSchema).default([]),
        edited: z.boolean().default(false),
        deleted: z.boolean().default(false),
      }),
    ),
    caseHistoryEntry(
      "task_created",
      z.object({
        taskId: z.string(),
        taskType: z.string(),
        priority: TaskPriority.nullable(),
        assignedTo: z.string().nullable(),
        aiAnalysis: TaskAiAnalysisSchema.nullable().describe(
          "Present once the task's AI analysis run has started; null for task types that don't run one",
        ),
      }),
    ),
    caseHistoryEntry(
      "task_assigned",
      z.object({
        taskId: z.string(),
        taskType: z.string(),
        assignedTo: z.string().nullable(),
        isReassignment: z
          .boolean()
          .describe(
            "False when this is the task's first-ever assignment, not a later reassignment",
          ),
      }),
    ),
    caseHistoryEntry(
      "task_updated",
      z.object({
        taskId: z.string(),
        taskType: z.string(),
        status: TaskStatus,
        waitingStatus: z.string().nullable(),
        notes: z.string().nullable(),
        updatedFields: z
          .array(TaskHistoryField)
          .describe("Stable identifiers for the task fields changed by this update"),
      }),
    ),
    caseHistoryEntry(
      "task_canceled",
      z.object({
        taskId: z.string(),
        taskType: z.string(),
        notes: z.string().nullable(),
      }),
    ),
    caseHistoryEntry(
      "task_reopened",
      z.object({
        taskId: z.string(),
        taskType: z.string(),
        notes: z.string().nullable(),
      }),
    ),
    caseHistoryEntry(
      "task_completed",
      z.object({
        taskId: z.string(),
        taskType: z.string(),
        solution: z.string().nullable(),
        notes: z.string().nullable(),
      }),
    ),
    caseHistoryEntry(
      "document_uploaded",
      z.object({
        documentId: z.string(),
        filename: z.string(),
        mimeType: z.string(),
        sizeBytes: z.number().int().nullable(),
      }),
    ),
    caseHistoryEntry(
      "document_classified",
      z.object({
        documentId: z.string(),
        filename: z.string(),
        documentType: DocumentTypeSchema,
        confidence: DocumentConfidenceLevelSchema,
      }),
    ),
    caseHistoryEntry(
      "document_classification_overridden",
      z.object({
        documentId: z.string(),
        filename: z.string(),
        documentType: DocumentTypeSchema,
      }),
    ),
    caseHistoryEntry(
      "document_deleted",
      z.object({ documentId: z.string(), filename: z.string() }),
    ),
    caseHistoryEntry(
      "conversation_recorded",
      z.object({
        contactId: z.string(),
        contactType: CaseContactType.nullable(),
        contactName: z.string().nullable(),
      }),
    ),
  ])
  .meta({ visibleTo: internalStaff });
export type CaseHistoryEntry = z.infer<typeof CaseHistoryEntrySchema>;
export type CaseHistoryEntryType = CaseHistoryEntry["type"];

export const ListCaseHistoryResponseSchema = z.object({
  items: z.array(CaseHistoryEntrySchema).meta({ visibleTo: internalStaff }),
});
